import { useState } from 'react';

import {
  type SelectedBattle,
  useSelection,
  useSelectionOptional,
} from '@/contexts/SelectionContext';

import { Button } from './ui/Button';

function ChevronUpIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

function getWinnerLoser(battle: SelectedBattle): {
  winner: string;
  loser: string;
} {
  if (battle.winner === 'a') {
    return {
      winner: battle.resultA.configName,
      loser: battle.resultB.configName,
    };
  } else if (battle.winner === 'b') {
    return {
      winner: battle.resultB.configName,
      loser: battle.resultA.configName,
    };
  }
  return { winner: 'Tie', loser: 'Tie' };
}

function SelectionItem({
  battle,
  index,
  onRemove,
}: {
  battle: SelectedBattle;
  index: number;
  onRemove: () => void;
}) {
  const { winner, loser } = getWinnerLoser(battle);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        padding: '0.5rem',
        borderBottom: '1px solid #E8E0D8',
        fontSize: '0.75rem',
      }}
    >
      <span
        style={{
          fontWeight: 600,
          color: '#6B6B6B',
          minWidth: '1.5rem',
        }}
      >
        {index + 1}.
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: '#1A1A1A',
            marginBottom: '0.25rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={battle.promptText}
        >
          {truncate(battle.promptText, 50)}
        </div>
        <div style={{ color: '#6B6B6B', fontSize: '0.6875rem' }}>
          <span style={{ color: '#2E7D4A' }}>W: {winner}</span>
          {' vs '}
          <span style={{ color: '#C62828' }}>L: {loser}</span>
        </div>
      </div>
      <button
        onClick={onRemove}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          color: '#A0A0A0',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Remove from selection"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

export function SelectionPanel() {
  const context = useSelectionOptional();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  if (!context) return null;

  const { selections, selectionCount, removeSelection, clearSelections } =
    context;

  if (selectionCount === 0) return null;

  const modelStats = new Map<
    string,
    { wins: number; losses: number; ties: number }
  >();

  for (const battle of selections) {
    const modelA = battle.resultA.configName;
    const modelB = battle.resultB.configName;

    if (battle.winner === 'a') {
      // A wins, B loses
      const statsA = modelStats.get(modelA) || { wins: 0, losses: 0, ties: 0 };
      statsA.wins++;
      modelStats.set(modelA, statsA);

      const statsB = modelStats.get(modelB) || { wins: 0, losses: 0, ties: 0 };
      statsB.losses++;
      modelStats.set(modelB, statsB);
    } else if (battle.winner === 'b') {
      // B wins, A loses
      const statsB = modelStats.get(modelB) || { wins: 0, losses: 0, ties: 0 };
      statsB.wins++;
      modelStats.set(modelB, statsB);

      const statsA = modelStats.get(modelA) || { wins: 0, losses: 0, ties: 0 };
      statsA.losses++;
      modelStats.set(modelA, statsA);
    } else {
      // Tie or both_bad
      const statsA = modelStats.get(modelA) || { wins: 0, losses: 0, ties: 0 };
      statsA.ties++;
      modelStats.set(modelA, statsA);

      const statsB = modelStats.get(modelB) || { wins: 0, losses: 0, ties: 0 };
      statsB.ties++;
      modelStats.set(modelB, statsB);
    }
  }

  const sortedModelStats = Array.from(modelStats.entries()).sort(
    (a, b) => b[1].wins + b[1].losses - (a[1].wins + a[1].losses)
  );

  const selectionsByCategory = new Map<string, typeof selections>();
  for (const battle of selections) {
    const category = battle.category || 'Uncategorized';
    const existing = selectionsByCategory.get(category) || [];
    existing.push(battle);
    selectionsByCategory.set(category, existing);
  }

  const sortedCategories = Array.from(selectionsByCategory.entries()).sort(
    (a, b) => b[1].length - a[1].length
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { exportSelections } = await import('@/utils/exportSelections');
      await exportSelections(selections);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClear = () => {
    if (
      window.confirm(
        `Are you sure you want to clear all ${selectionCount} selections?`
      )
    ) {
      clearSelections();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        width: isExpanded ? '360px' : 'auto',
        maxHeight: isExpanded ? '60vh' : 'auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          background: '#8B2332',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
          }}
        >
          <CheckIcon />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {selectionCount} Battle{selectionCount !== 1 ? 's' : ''} Selected
          </span>
        </div>
        {isExpanded ? <ChevronDownIcon /> : <ChevronUpIcon />}
      </button>

      {isExpanded && (
        <>
          <div
            style={{
              padding: '0.75rem',
              borderBottom: '1px solid #E8E0D8',
              background: '#F5F0EB',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#6B6B6B',
                marginBottom: '0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Model Coverage ({modelStats.size} models)
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                maxHeight: '120px',
                overflowY: 'auto',
              }}
            >
              {sortedModelStats.map(([model, stats]) => (
                <div
                  key={model}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    padding: '0.25rem 0',
                  }}
                >
                  <span
                    style={{
                      color: '#1A1A1A',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '160px',
                    }}
                    title={model}
                  >
                    {model}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span
                      style={{
                        color: '#2E7D4A',
                        fontWeight: 600,
                      }}
                      title="Wins"
                    >
                      W:{stats.wins}
                    </span>
                    <span
                      style={{
                        color: '#C62828',
                        fontWeight: 600,
                      }}
                      title="Losses"
                    >
                      L:{stats.losses}
                    </span>
                    {stats.ties > 0 && (
                      <span
                        style={{
                          color: '#6B6B6B',
                          fontWeight: 500,
                        }}
                        title="Ties"
                      >
                        T:{stats.ties}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: '200px',
            }}
          >
            {sortedCategories.map(([category, categoryBattles]) => (
              <div key={category}>
                <div
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#F9F7F5',
                    borderBottom: '1px solid #E8E0D8',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    color: '#6B6B6B',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    position: 'sticky',
                    top: 0,
                  }}
                >
                  <span>{category}</span>
                  <span
                    style={{
                      background: '#E8E0D8',
                      padding: '0 6px',
                      borderRadius: '10px',
                      fontSize: '0.625rem',
                    }}
                  >
                    {categoryBattles.length}
                  </span>
                </div>
                {categoryBattles.map((battle, index) => (
                  <SelectionItem
                    key={battle.id}
                    battle={battle}
                    index={index}
                    onRemove={() => removeSelection(battle.id)}
                  />
                ))}
              </div>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              padding: '0.75rem',
              borderTop: '1px solid #E8E0D8',
              background: '#FAFAFA',
            }}
          >
            <Button
              variant="secondary"
              size="small"
              onClick={handleClear}
              style={{ flex: 1 }}
            >
              Clear All
            </Button>
            <Button
              size="small"
              onClick={handleExport}
              disabled={isExporting}
              style={{
                flex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <DownloadIcon />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function BattleSelectionCheckbox({
  battle,
  style,
}: {
  battle: Omit<SelectedBattle, 'selectedAt'>;
  style?: React.CSSProperties;
}) {
  const { isSelected, toggleSelection } = useSelection();
  const selected = isSelected(battle.id);

  return (
    <button
      onClick={e => {
        e.stopPropagation();
        toggleSelection(battle);
      }}
      style={{
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        border: selected ? '2px solid #8B2332' : '2px solid #D0D0D0',
        background: selected ? '#8B2332' : 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s ease',
        ...style,
      }}
      title={selected ? 'Remove from selection' : 'Add to selection'}
    >
      {selected && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      )}
    </button>
  );
}

export function AddToSelectionButton({
  battle,
  style,
}: {
  battle: Omit<SelectedBattle, 'selectedAt'>;
  style?: React.CSSProperties;
}) {
  const { isSelected, toggleSelection } = useSelection();
  const selected = isSelected(battle.id);

  return (
    <Button
      variant={selected ? 'primary' : 'secondary'}
      size="small"
      onClick={() => toggleSelection(battle)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        ...style,
      }}
    >
      {selected ? (
        <>
          <CheckIcon /> Selected
        </>
      ) : (
        <>+ Add to Selection</>
      )}
    </Button>
  );
}
